/**
 * Facts about the real exam (Tentamen i Modern fysik, LGFY41/L9FY41/L9FY45).
 * Imported by the content validator, the server (generator, verdict) and the client.
 */

export const EXAM_TOTAL_POINTS = 18;
export const THRESHOLD_G = 8.5;
export const THRESHOLD_VG = 13.5;
export const EXAM_DURATION_MINUTES = 300; // 08:30–13:30
export const EXAM_PROBLEM_COUNT = 7;

export type SlotPoints = 2 | 3;
export interface ExamSlot {
  /** 1-based problem number on the paper. */
  number: number;
  points: SlotPoints;
  /** Primary chapters that have filled this slot on the real exams (HT22, HT23, HT24, HT25). */
  chapters: number[];
}

/** Slot template derived from the three real exams: points pattern 3,2,3,2,3,2,3 = 18. */
export const EXAM_SLOTS: ExamSlot[] = [
  { number: 1, points: 3, chapters: [1, 2] },
  { number: 2, points: 2, chapters: [2] },
  { number: 3, points: 3, chapters: [1, 2] },
  { number: 4, points: 2, chapters: [2, 3, 4] },
  { number: 5, points: 3, chapters: [2, 3, 4] },
  { number: 6, points: 2, chapters: [5] },
  { number: 7, points: 3, chapters: [5, 6, 7] },
];

/** HT24 had four chapter-2 problems, so the cap is 4, not 3. */
export const MAX_PROBLEMS_PER_CHAPTER = 4;

/** Questions attempted or revealed within this window are avoided by the generator when alternatives exist. */
export const SEEN_WINDOW_DAYS = 30;

/**
 * Autosave keeps working this long past the deadline before the session is
 * expired (spec §4.5). Shared because more than the exam feature needs to know
 * whether a session is still live — practice refuses to reveal a problem the
 * student is sitting right now.
 */
export const EXAM_GRACE_MS = 60_000;

/** Minimum eligible original open questions per slot pool for a healthy bank (validator warns below this). */
export const MIN_POOL_PER_SLOT = 6;

/** Bonus points from hand-in assignments: 0, 1, 1.5 or 2. */
export const BONUS_POINT_OPTIONS = [0, 1, 1.5, 2] as const;
export type BonusPoints = (typeof BONUS_POINT_OPTIONS)[number];

export type Verdict = "U" | "G" | "VG";

/**
 * Real course rule: bonus points only ever lift a result to G, never to VG, and are
 * only applied when the raw score is at most 2 points below the G threshold.
 */
export function verdictFor(
  rawScore: number,
  bonus: BonusPoints = 0,
): { verdict: Verdict; bonusApplied: number } {
  if (rawScore >= THRESHOLD_VG) return { verdict: "VG", bonusApplied: 0 };
  if (rawScore >= THRESHOLD_G) return { verdict: "G", bonusApplied: 0 };
  if (bonus > 0 && rawScore >= THRESHOLD_G - 2 && rawScore + bonus >= THRESHOLD_G) {
    return { verdict: "G", bonusApplied: bonus };
  }
  return { verdict: "U", bonusApplied: 0 };
}

/** How far a graded paper is from the next grade (`pointsToNextGrade`). */
export type GradeGap = { toVG: number } | { toG: number };

/**
 * Points to the next grade, from the raw score and the **stored** verdict only,
 * so a later change of the user's bonus can never contradict the badge
 * (pedagogy spec §7.3.3, §12 C16). VG → null. G → the gap to VG (bonus never
 * lifts to VG). U → the gap to G without bonus points, because the bonus in
 * force at grading is not stored for a U.
 */
export function pointsToNextGrade(raw: number, verdict: Verdict): GradeGap | null {
  if (verdict === "VG") return null;
  if (verdict === "G") return { toVG: Math.round((THRESHOLD_VG - raw) * 100) / 100 };
  return { toG: Math.round((THRESHOLD_G - raw) * 100) / 100 };
}

/** Real past exams as slot assignments (chapter per problem) — each must be a legal generator output. */
export const REAL_EXAM_CHAPTERS: Record<string, number[]> = {
  HT22: [2, 2, 2, 4, 3, 5, 6],
  HT23: [1, 2, 2, 2, 4, 5, 6],
  HT24: [1, 2, 2, 2, 2, 5, 5],
  HT25: [1, 2, 2, 4, 4, 5, 5],
};
